import { mergeAttributes, Node, Range } from "@tiptap/core"
import { Node as ProseMirrorNode } from "@tiptap/pm/model"
import { PluginKey } from "@tiptap/pm/state"
import { Editor } from "@tiptap/core"

import Model from "../../../../../../model/model"
import { ID, Idea } from "../../../../../../model/types"
import { Suggestion, SuggestionOptions } from "./suggestion"
import { clearPageLinkFromDOM, updateNodeLabelFromDOM } from "./text_helpers"

export type MentionOptions = {
  HTMLAttributes: Record<string, any>
  renderLabel: (props: {
    options: MentionOptions
    node: ProseMirrorNode
  }) => string
  suggestion: Omit<SuggestionOptions, "editor">
}

export const MentionPluginKey = new PluginKey("page-link")

// Find page links in a given editor element and look for ideas with updated titles or that
// have been deleted.
export const cleanUpPageLinks = (
  editorElement: HTMLDivElement,
  editor: Editor,
  model: Model
) => {
  const pageLinkElements = Array.from(
    editorElement.querySelectorAll("[data-type='page-link']")
  ) as HTMLAnchorElement[]
  pageLinkElements.forEach((el: HTMLAnchorElement) => {
    if (el.dataset.type === "page-link") {
      const idea = model.getIdea(el.dataset.id as ID)
      if (idea == null) {
        console.log(
          `Dead page link "${el.dataset.label}" replaced with plain text`
        )
        clearPageLinkFromDOM(editor, el)
      } else if (idea.title !== el.dataset.label) {
        console.log(
          `Page link "${el.dataset.label}" updated to "${idea.title}"`
        )
        updateNodeLabelFromDOM(editor, el, idea.title)
      }
    }
  })
}

// The main inserter of page links
export const replaceRangeWithPageLinkToIdea = (
  editor: Editor,
  range: Range,
  idea: Idea
) => {
  // increase range.to by one when the next node is of type "text"
  // and starts with a space character
  const nodeAfter = editor.view.state.selection.$to.nodeAfter
  const overrideSpace = nodeAfter?.text?.startsWith(" ")

  if (overrideSpace) {
    range.to += 1
  }

  editor
    .chain()
    .focus()
    .insertContentAt(range, [
      {
        type: "page-link",
        attrs: {
          id: idea.id,
          label: idea.title,
        },
      },
      {
        type: "text",
        text: " ",
      },
    ])
    .run()

  window.getSelection()?.collapseToEnd()
}

export const PageLink = Node.create<MentionOptions>({
  name: "page-link",

  addOptions() {
    return {
      HTMLAttributes: {},
      renderLabel({ options, node }) {
        return `${options.suggestion.char}${node.attrs.label ?? node.attrs.id}`
      },
      suggestion: {
        char: "@",
        pluginKey: MentionPluginKey,
        command: ({ editor, range, props }) => {
          throw "Shouldn't be called"
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from)
          const type = state.schema.nodes[this.name]
          const allow = !!$from.parent.type.contentMatch.matchType(type)

          return allow
        },
      },
    }
  },

  group: "inline",

  inline: true,

  selectable: true,

  atom: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-id"),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {}
          }

          return {
            "data-id": attributes.id,
          }
        },
      },

      label: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-label"),
        renderHTML: (attributes) => {
          if (!attributes.label) {
            return {}
          }

          return {
            "data-label": attributes.label,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `a[data-type="page-link"]`,
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "a",
      mergeAttributes(
        { "data-type": this.name },
        this.options.HTMLAttributes,
        HTMLAttributes
      ),
      this.options.renderLabel({
        options: this.options,
        node,
      }),
    ]
  },

  renderText({ node }) {
    return this.options.renderLabel({
      options: this.options,
      node,
    })
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isMention = false
          const { selection } = state
          const { empty, anchor } = selection

          if (!empty) {
            return false
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isMention = true
              tr.insertText(
                this.options.suggestion.char || "",
                pos,
                pos + node.nodeSize
              )

              return false
            }
          })

          return isMention
        }),
    }
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ]
  },
})
